<template>
  <section id="dashboard">
    <div v-if="userData.roles[0].id == 1">
      <superAdmin :isLoading="isLoading" :userData="userData" :dataDashboard="dataDashboard" @buscar="buscar($event)" />
    </div>
    <div v-if="userData.roles[0].id == 2">
      <admin :isLoading='isLoading' :userData="userData" :dataDashboard="dataDashboard" @buscar="buscar($event)" />
    </div>
    <div v-if="userData.roles[0].id == 3">
      <oma :isLoading='isLoading' :userData="userData" :dataDashboard="dataDashboard" @buscar="buscar($event)" />
    </div>
    <div v-if="userData.roles[0].id == 4">
      <nurse :isLoading='isLoading' :userData="userData" :dataDashboard="dataDashboard" @buscar="buscar($event)" />
    </div>
    <div v-if="userData.roles[0].id == 5">
      <frontdesk :isLoading='isLoading' :userData="userData" :dataDashboard="dataDashboard" @buscar="buscar($event)" />
    </div>
    
    <!-- Progressive loading component -->
    <ProgressiveLoader
      :showLoader="showProgressiveLoader"
      :currentProgress="progressiveProgress.current"
      :totalMonths="progressiveProgress.total"
      :currentMonth="progressiveProgress.currentMonth"
      :currentYear="progressiveProgress.currentYear"
      :allowCancel="true"
      @cancel="cancelProgressiveLoading"
    />
    
    <!-- Progressive notification -->
    <ProgressiveNotification
      :showNotification="showProgressiveNotification"
      @close="closeProgressiveNotification"
    />
  </section>
</template>

<script>
import axios from "@/core/services/api/dashboard";
import progressiveLoader from "@/core/services/api/dashboard/progressiveLoader";
import {
  daysBetween as calculateDaysBetween,
  monthsBetween as calculateMonthsBetween,
} from "@/core/utils/dashboardDates";
import ProgressiveLoader from "./components/ProgressiveLoader.vue";
import ProgressiveNotification from "./components/ProgressiveNotification.vue";
import superAdmin from "./superAdmin/superAdmin.vue";
import admin from "./admin/admin.vue";
import frontdesk from "./frontdesk/frontdesk.vue";
import nurse from "./nurse/nurse.vue";
import oma from "./oma/oma.vue";
// Import mobile optimizations CSS
import "./assets/mobileComponentOptimizations.css";

export default {
  components: {
    admin,
    frontdesk,
    nurse,
    oma,
    superAdmin,
    ProgressiveLoader,
    ProgressiveNotification,
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("userData")),
      dataDashboard: {},
      isLoading: false,
      showProgressiveLoader: false,
      showProgressiveNotification: false,
      progressiveProgress: {
        current: 0,
        total: 0,
        currentMonth: 0,
        currentYear: 0
      },
      progressiveLoadingController: null
    };
  },
  created() {
    // Eliminamos la búsqueda automática para que solo se ejecute cuando el usuario presione buscar
    // if (this.userData.roles[0].id != 2) this.buscar({ start: `${new Date().getFullYear()}-${new Date().getMonth() + 1}-1`, end: `${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}` });
  },
  methods: {
    async buscar({ start, end, facilities }) {
      // Cancel previous progressive loading if exists
      if (this.progressiveLoadingController) {
        this.progressiveLoadingController.abort();
      }

      // Create new controller for cancellation
      const controller = new AbortController();
      this.progressiveLoadingController = controller;
      const isCurrent = () => this.progressiveLoadingController === controller;

      try {
        // Set up progressive loading callbacks
        progressiveLoader.onProgress((progress) => {
          if (!isCurrent()) return;
          this.progressiveProgress = {
            current: progress.current,
            total: progress.total,
            currentMonth: progress.currentMonth,
            currentYear: progress.currentYear
          };
        });
        progressiveLoader.onComplete(null);
        progressiveLoader.onError(null);

        // Determine if to use progressive loading based on device and date range
        const daysBetween = calculateDaysBetween(start, end);

        // Check if device is mobile
        const isMobile = this.isMobileDevice();

        // Calculate months between dates
        const monthsBetween = calculateMonthsBetween(start, end);

        // Use progressive loading for ranges larger than 15 days on desktop
        const shouldUseProgressive = daysBetween > 15 && !isMobile;

        // Show modal only for ranges larger than 2 months
        const shouldShowModal = monthsBetween > 2;

        const userId = (this.userData.roles[0].id != 1 && this.userData.roles[0].id != 2) ? this.userData.id : null;
        this.isLoading = true;
        let result;

        if (shouldUseProgressive) {
          // Show modal and notification only for ranges larger than 2 months
          if (shouldShowModal) {
            this.showProgressiveLoader = true;
            this.showProgressiveNotification = true;
            this.progressiveProgress = {
              current: 0,
              total: progressiveLoader.getBlockRanges(start, end).length,
              currentMonth: 0,
              currentYear: 0
            };
          }

          result = await progressiveLoader.loadProgressiveData(
            { start, end, facilities },
            userId,
            shouldShowModal,
            controller.signal
          );
        } else {
          // Use normal loading for mobile or smaller ranges
          const datarda = {
            start_date: start,
            end_date: end,
            facilities
          };
          if (userId) datarda.user_id = userId;

          const { data } = await axios.getData(datarda, { signal: controller.signal });
          result = data;
        }

        if (!isCurrent()) return;
        this.dataDashboard = result;
      } catch (error) {
        if (error?.name === 'AbortError' || !isCurrent()) return;
        console.error('Error loading dashboard data:', error);

        this.$bvToast.toast(
          error?.response?.data?.message || 'Error loading data. Please try again.',
          {
            title: 'Loading Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          }
        );
      } finally {
        if (isCurrent()) {
          this.progressiveLoadingController = null;
          this.isLoading = false;
          this.showProgressiveLoader = false;
          this.showProgressiveNotification = false;
        }
      }
    },

    isMobileDevice() {
      // Check if device is mobile based on screen size and user agent
      const isMobileByScreen = window.innerWidth <= 768;
      const isMobileByAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      return isMobileByScreen || isMobileByAgent;
    },

    cancelProgressiveLoading() {
      if (this.progressiveLoadingController) {
        this.progressiveLoadingController.abort();
        this.progressiveLoadingController = null;
      }
      this.isLoading = false;
      this.showProgressiveLoader = false;
      this.showProgressiveNotification = false;
      
      this.$bvToast.toast('Loading cancelled by user.', {
        title: 'Loading Cancelled',
        variant: 'warning',
        solid: true,
        autoHideDelay: 3000
      });
    },

    closeProgressiveNotification() {
      this.showProgressiveNotification = false;
    },
  },
};
</script>
